import { connect, Field, Item, OnBootCtx } from "datocms-plugin-sdk";
import { render } from './utils/render';
import ConfigScreen from './entrypoints/ConfigScreen';
import { summarizeNestedBlocks } from './nestedBlocks';
import 'datocms-react-ui/styles.css';

export interface AmpExp {
  expId?: string;
  variants: { [key: string]: string };
}

const fieldsCache = new Map<string, Promise<Field[]>>();

function loadFields(ctx: OnBootCtx, itemTypeId: string): Promise<Field[]> {
  let fields = fieldsCache.get(itemTypeId);
  if (!fields) {
    fields = ctx.loadItemTypeFields(itemTypeId);
    // Drop a rejected promise so a transient failure can retry
    fields.catch(() => fieldsCache.delete(itemTypeId));
    fieldsCache.set(itemTypeId, fields);
  }
  return fields;
}

// Load the block's fields, then summarize its direct children (null if none)
async function buildNestedBlocksSummary(item: Item, ctx: OnBootCtx): Promise<string | null> {
  const itemTypeId = item.attributes.itemTypeId as string;

  let fields: Field[];
  try {
    fields = await loadFields(ctx, itemTypeId);
  } catch {
    return null;
  }

  return summarizeNestedBlocks(
    fields,
    item.attributes as Record<string, any>,
    (id) => ctx.itemTypes[id]?.attributes.name ?? null,
  );
}

connect({
  renderConfigScreen(ctx) {
    return render(<ConfigScreen ctx={ctx} />);
  },

  async buildItemPresentationInfo(item: Item, ctx: OnBootCtx) {
    const params = ctx.plugin.attributes.parameters;
    const ampExpIsActive = params.ampExpBlockNameCustomizerIsActive as boolean;
    const nestedBlocksIsActive = params.nestedBlocksTitleIsActive as boolean;
    if (!ampExpIsActive && !nestedBlocksIsActive) return;

    // item.attributes.itemTypeId is truthy for blocks; models do not have a value here
    const itemTypeId = item.attributes.itemTypeId as string | undefined;
    if (!itemTypeId) return;

    let ampExpTitle: string | null = null;
    if (ampExpIsActive) {
      const variantField = item.attributes['amp_exp'] as string | null;
      if (variantField) {
        try {
          const variantObj: AmpExp = JSON.parse(variantField);
          const key = Object.keys(variantObj.variants)[0];
          ampExpTitle = `🚧 ${key}: ${variantObj.variants[key]}`;
        } catch {
          // Fall through so DatoCMS can still show the nested summary
          // or the default block title
        }
      }
    }

    let nestedSummary: string | null = null;
    if (nestedBlocksIsActive) {
      nestedSummary = await buildNestedBlocksSummary(item, ctx);
    }

    if (!ampExpTitle && !nestedSummary) return;

    // The custom title only fills the slot after Dato's fixed block-type
    // label, so the block name must not be repeated here
    const parts = [ampExpTitle, nestedSummary && `🪺 ${nestedSummary}`].filter(Boolean);
    return {
      title: parts.join(' '),
      imageUrl: '',
    };
  },
});
